import { h, clear, mount, formatTime, formatDuration } from '../ui/dom.js';
import {
  card,
  button,
  emptyState,
  toast,
  badge,
  readinessBadge,
  metric,
} from '../ui/components.js';
import { api, getAuthToken } from '../api.js';
import { kindIcon, downloadAsset } from '../ui/media.js';
import type { Page, PageContext } from '../page.js';
import { MEDIA_KIND_LABELS, type MediaAsset, type SupportItem, type SupportQueue } from '../types.js';

export async function supportPage(context: PageContext): Promise<Page> {
  const { id } = context.params;
  if (!id) return { title: 'Painel de apoio', body: emptyState('Edição não informada.') };

  const container = h('div', { class: 'stack' });
  let stream: EventSource | null = null;

  // Indicador de conexao reutilizado a cada renderizacao.
  const statusChip = h('span', {
    class: 'media-chip',
    text: '○ conectando…',
    style: { color: 'var(--text-muted)' },
  });

  const setConnectionState = (connected: boolean): void => {
    statusChip.textContent = connected ? '● tempo real ativo' : '○ reconectando…';
    statusChip.style.color = connected ? 'var(--success)' : 'var(--text-muted)';
  };

  const render = (data: SupportQueue): void => {
    const current = data.items.find((item) => item.isCurrent);
    const next = data.items.find((item) => item.isNext);

    clear(container);

    container.append(
      h(
        'div',
        { class: 'grid grid--4' },
        metric('Ministrações na fila', data.items.length, 'accent'),
        metric('Atual', current ? formatTime(current.scheduledStart) : '—', 'info'),
        metric('Materiais disponíveis', data.items.reduce((sum, item) => sum + item.mediaCount, 0), 'success'),
        metric(
          'Pendências',
          data.items.filter((item) => item.readiness !== 'HOMOLOGADO').length,
          data.items.some((item) => item.readiness !== 'HOMOLOGADO') ? 'warning' : 'default',
        ),
      ),
    );

    container.append(
      card({
        title: 'Agora no palco',
        hint: 'As informações são atualizadas automaticamente em tempo real.',
        actions: [statusChip],
        body: current
          ? renderFocus(current)
          : emptyState('Nenhuma ministração em andamento neste momento.', '◷'),
      }),
    );

    if (next) {
      container.append(
        card({
          title: 'A seguir',
          hint: 'Prepare a projeção e as trilhas com antecedência.',
          body: renderFocus(next, true),
        }),
      );
    }

    container.append(
      card({
        title: 'Fila completa do encontro',
        hint: 'Acesso direto aos slides homologados, áudios e vídeos de introdução.',
        body: h('div', { class: 'list' }, ...data.items.map((item) => renderQueueItem(item))),
      }),
    );
  };

  const renderFocus = (item: SupportItem, compact = false): HTMLElement =>
    h(
      'div',
      { class: 'stack' },
      h(
        'div',
        { class: 'row row--between' },
        h(
          'div',
          null,
          h('div', { style: { fontSize: compact ? '16px' : '20px', fontWeight: '700' }, text: item.title }),
          h('div', {
            class: 'muted small',
            text: `${formatTime(item.scheduledStart)} — ${formatTime(item.scheduledEnd)} · ${formatDuration(item.durationMin)} · ${item.ministradorName ?? 'Sem ministrador'}`,
          }),
        ),
        readinessBadge(item.readiness),
      ),
      renderMaterials(item.media),
    );

  const renderMaterials = (media: MediaAsset[]): HTMLElement => {
    if (media.length === 0) {
      return h('div', { class: 'muted small', text: 'Nenhum material anexado a esta ministração.' });
    }

    const grouped = new Map<string, MediaAsset[]>();
    for (const asset of media) {
      const list = grouped.get(asset.kind) ?? [];
      list.push(asset);
      grouped.set(asset.kind, list);
    }

    return h(
      'div',
      { class: 'stack' },
      ...[...grouped.entries()].map(([kind, assets]) =>
        h(
          'div',
          { class: 'stack stack--sm' },
          h('div', { class: 'card__hint', text: MEDIA_KIND_LABELS[kind as MediaAsset['kind']] }),
          h(
            'div',
            { class: 'row' },
            ...assets.map((asset) =>
              h(
                'div',
                { class: 'row', style: { gap: '6px' } },
                asset.embedUrl
                  ? button(`${kindIcon(asset.kind)} Abrir`, {
                      size: 'sm',
                      variant: 'ghost',
                      onClick: () => window.open(asset.embedUrl as string, '_blank', 'noopener'),
                    })
                  : null,
                !asset.embedUrl && asset.fileUrl
                  ? button(`${kindIcon(asset.kind)} ${asset.title}`, {
                      size: 'sm',
                      variant: 'subtle',
                      onClick: () => void downloadAsset(asset),
                    })
                  : null,
                asset.embedUrl ? h('span', { class: 'muted small', text: asset.title }) : null,
              ),
            ),
          ),
        ),
      ),
    );
  };

  const renderQueueItem = (item: SupportItem): HTMLElement =>
    h(
      'div',
      { class: 'list__item' },
      h(
        'div',
        {
          class: 'list__index',
          text: String(item.position),
          style: item.isCurrent ? { background: 'var(--accent-soft)', color: 'var(--accent)' } : {},
        },
      ),
      h(
        'div',
        { class: 'list__body' },
        h(
          'div',
          { class: 'row' },
          h('span', { class: 'list__title', text: item.title }),
          item.isCurrent ? badge('No palco', 'accent', true) : null,
          item.isNext ? badge('A seguir', 'info') : null,
        ),
        h(
          'div',
          { class: 'row small muted' },
          h('span', { class: 'mono', text: `${formatTime(item.scheduledStart)} — ${formatTime(item.scheduledEnd)}` }),
          h('span', { text: '·' }),
          h('span', { text: item.ministradorName ?? 'Sem ministrador' }),
          h('span', { text: '·' }),
          h('span', { text: `${item.mediaCount} material(is)` }),
        ),
      ),
      h(
        'div',
        { class: 'table__actions' },
        item.media.some((asset) => asset.fileUrl)
          ? button('Downloads', {
              size: 'sm',
              variant: 'ghost',
              onClick: () =>
                void (async () => {
                  for (const asset of item.media.filter((a) => a.fileUrl)) {
                    await downloadAsset(asset);
                  }
                })(),
            })
          : null,
        button('Púlpito', {
          size: 'sm',
          variant: 'subtle',
          onClick: () => context.navigate(`/ministracoes/${item.id}/pulpito`),
        }),
      ),
    );

  const load = async (): Promise<void> => {
    try {
      const data = await api.get<SupportQueue>(`/api/editions/${id}/support`);
      render(data);
    } catch (error) {
      clear(container);
      mount(
        container,
        card({
          title: 'Não foi possível carregar o painel',
          body: h('div', { text: error instanceof Error ? error.message : 'Erro inesperado.' }),
        }),
      );
    }
  };

  const connect = (): void => {
    const token = getAuthToken();
    if (!token) return;

    stream = new EventSource(`/api/realtime/editions/${id}?token=${encodeURIComponent(token)}`);
    stream.addEventListener('schedule.updated', () => void load());
    stream.addEventListener('readiness.updated', () => void load());
    stream.addEventListener('media.updated', () => void load());
    stream.addEventListener('edition.updated', () => void load());
    stream.addEventListener('ready', () => {
      setConnectionState(true);
      toast.info('Painel conectado', 'Atualizações em tempo real ativas.');
    });
    stream.onerror = () => {
      setConnectionState(false);
      if (stream) {
        stream.close();
        stream = null;
      }
      setTimeout(() => {
        if (!stream) connect();
      }, 5000);
    };
  };

  await load();
  connect();

  // Encerra o canal quando a tela sai do ar.
  const hashAtEntry = window.location.hash;
  const watcher = setInterval(() => {
    if (window.location.hash !== hashAtEntry) {
      stream?.close();
      stream = null;
      clearInterval(watcher);
    }
  }, 1200);

  return {
    title: 'Painel da Equipe de Apoio',
    subtitle: 'Console de mídia e som com a fila do encontro em tempo real.',
    actions: [
      button('Atualizar', { variant: 'ghost', size: 'sm', onClick: () => void load() }),
      button('Check-list', {
        variant: 'ghost',
        size: 'sm',
        onClick: () => context.navigate(`/edicoes/${id}/prontidao`),
      }),
      button('Edição', { variant: 'ghost', size: 'sm', onClick: () => context.navigate(`/edicoes/${id}`) }),
    ],
    body: container,
  };
}
